/** @format */

import type { LoginAdmin } from "infrastructure/interfaces/login.interface";
import storage from "infrastructure/utils/storage";

export const ADMIN_NAME_STORAGE_KEY = "admin_name";

type AdminLike = Record<string, unknown>;

const readString = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

export const formatAdminFullName = (admin: LoginAdmin | null | undefined): string => {
  if (!admin) {
    return "";
  }

  const fullName = [admin.firstName, admin.lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ")
    .trim();

  return fullName || admin.email?.trim() || "";
};

const readNamePart = (admin: AdminLike, camelKey: string, snakeKey: string): string =>
  readString(admin[camelKey]) || readString(admin[snakeKey]);

export const getAdminDisplayName = (admin: AdminLike | null | undefined): string => {
  if (!admin) {
    return "";
  }

  const firstName = readNamePart(admin, "firstName", "first_name");
  const lastName = readNamePart(admin, "lastName", "last_name");
  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

  if (fullName) {
    return fullName;
  }

  const name = readString(admin.name);
  if (name) {
    return name;
  }

  return readString(admin.email);
};

export const extractLoginAdmin = (
  data: Record<string, unknown>
): LoginAdmin | undefined => {
  const nestedAdmin = data.admin;
  if (nestedAdmin && typeof nestedAdmin === "object") {
    return nestedAdmin as LoginAdmin;
  }

  const response = data.response;
  if (response && typeof response === "object") {
    const responseAdmin = (response as Record<string, unknown>).admin;
    if (responseAdmin && typeof responseAdmin === "object") {
      return responseAdmin as LoginAdmin;
    }
  }

  if (typeof data.firstName === "string" || typeof data.email === "string") {
    return data as LoginAdmin;
  }

  return undefined;
};

export const extractLoginPayload = (
  data: Record<string, unknown>
): Record<string, unknown> => {
  if (data.response && typeof data.response === "object") {
    return data.response as Record<string, unknown>;
  }

  return data;
};

export const persistAdminSessionFromLogin = (data: Record<string, unknown>): void => {
  const admin = extractLoginAdmin(data);
  const displayName = formatAdminFullName(admin);

  if (displayName) {
    storage.setItem(ADMIN_NAME_STORAGE_KEY, displayName);
    return;
  }

  storage.clearItem(ADMIN_NAME_STORAGE_KEY);
};
